import React, { useState, useEffect } from "react";
import { addTeamCreditBySuperAdmin } from "services/billing";
import { getAllTeams, deleteTeamBySuperAdmin } from "services/configuration";
import SearchBar from "components/common/SearchBar";
import allImgPaths from "assets/images";
import Toast from "components/common/Toast";
import { Modal } from "components/common/modals/Modal";
import { HeaderModal } from "components/common/modals/Header";
import { Body } from "components/common/modals/Body";
import { FooterModal } from "components/common/modals/Footer";
import { InputTextField } from "components/InputField";
import Textarea from "components/common/Textarea";
import close from "assets/images/close-icon.svg";

interface Team {
  id: string;
  name: string;
  adminName?: string;
  adminEmail?: string;
}

const AddCreditToTeam: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [creditAmount, setCreditAmount] = useState<any>("");
  const [creditMessage, setCreditMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Fetch all teams on component mount
  useEffect(() => {
    fetchTeams();
  }, [searchTerm]);

  // Fetch teams from API
  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await getAllTeams(searchTerm);
      if (response?.data) {
        const formattedTeams = response?.data?.map((team: any) => ({
          id: team._id,
          name: team.name,
          adminName: team.created_by?.name,
          adminEmail: team.created_by?.email,
        }));
        setTeams(formattedTeams);
        setFilteredTeams(formattedTeams);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      Toast.error("Error", "Failed to fetch teams. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (value: any) => {
    setSearchTerm(value);
  };

  // Open modal for adding credit
  const openAddCreditModal = (team: Team) => {
    setSelectedTeam(team);
    setIsModalOpen(true);
    setCreditAmount("");
    setCreditMessage("");
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTeam(null);
  };

  // Open delete confirmation modal
  const openDeleteModal = (team: Team) => {
    setTeamToDelete(team);
    setIsDeleteModalOpen(true);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTeamToDelete(null);
  };

  // Handle team deletion
  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;

    try {
      setIsDeleting(true);
      const response: any = await deleteTeamBySuperAdmin(teamToDelete.id);

      if (response?.success) {
        Toast.success(
          "Success",
          `Team "${teamToDelete.name}" has been deleted successfully.`
        );
        closeDeleteModal();
        fetchTeams();
      } else {
        Toast.error("Error", response?.message || "Failed to delete team.");
      }
    } catch (error) {
      console.error("Error deleting team:", error);
      Toast.error("Error", "Failed to delete team. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle form submission for adding credit
  const handleAddCredit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTeam || !creditAmount || !creditMessage) {
      Toast.error("Error", "Please fill in all fields");
      return;
    }

    try {
      setIsSubmitting(true);

      await addTeamCreditBySuperAdmin({
        teamId: selectedTeam.id,
        amount: Number(creditAmount),
        description: creditMessage,
      });

      Toast.success(
        "Success",
        `${creditAmount} ${creditAmount > 1 ? "credits" : "credit"} added to ${
          selectedTeam?.name
        } successfully.`
      );

      // Close modal and reset form
      closeModal();

      // Refresh teams list
      fetchTeams();
    } catch (error) {
      console.error("Error adding credit:", error);
      Toast.error("Error", "Failed to add credit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header Section with Search */}
      <div className="mb-6 flex justify-between items-center">
        <SearchBar
          img_url={allImgPaths.searchIcon}
          value={searchTerm}
          onChange={handleSearchChange}
          width={"w-96"}
          placeholder={"Search teams by name..."}
        />
      </div>

      {/* Teams List */}
      <div className="border border-[#EAECF0] rounded-lg overflow-hidden mb-2">
        {loading ? (
          <div className="p-4 text-center">Loading teams...</div>
        ) : filteredTeams.length > 0 ? (
          <div className="overflow-y-auto h-[calc(100vh_-_290px)]">
            <table className="w-full table-fixed" cellPadding={1} border={0}>
              <thead className="bg-[#F9FAFB] border-b border-[#EAECF0] sticky top-0">
                <tr>
                  <th className="w-[25%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                    Team Name
                  </th>
                  <th className="w-[25%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                    Team Admin Name
                  </th>
                  <th className="w-[30%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                    Team Admin Email
                  </th>
                  <th className="w-[20%] text-right py-3 pr-6 font-interMedium text-sm text-[#475467]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTeams.map((team, index) => (
                  <tr
                    key={team.id}
                    className={`border-b border-[#EAECF0] ${
                      index % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"
                    }`}
                  >
                    <td
                      className="py-4 px-6 text-sm text-[#475467] truncate"
                      title={team.name}
                    >
                      {team.name}
                    </td>
                    <td
                      className="py-4 px-6 text-sm text-[#475467] truncate"
                      title={team.adminName || "N/A"}
                    >
                      {team.adminName || "N/A"}
                    </td>
                    <td
                      className="py-4 px-6 text-sm text-[#475467] truncate"
                      title={team.adminEmail || "N/A"}
                    >
                      {team.adminEmail || "N/A"}
                    </td>
                    <td className="py-4 px-6 text-sm text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openAddCreditModal(team)}
                          className="!text-sm text-white bg-[#1751D0] hover:bg-[#113B98] font-interMedium rounded-lg px-3.5 py-2"
                        >
                          Add Credit
                        </button>
                        <button
                          onClick={() => openDeleteModal(team)}
                          className="!text-sm text-white bg-red-600 hover:bg-red-700 font-interMedium rounded-lg px-3.5 py-2"
                        >
                          Delete Team
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center min-h-[calc(100vh_-_290px)] flex items-center justify-center">
            <div className="flex flex-col items-center justify-center">
              <p className="font-interMedium text-base text-[#475467]">
                No teams found
              </p>
              <p className="text-sm text-[#667085]">
                No teams found matching your search.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Team Confirmation Modal */}
      {isDeleteModalOpen && teamToDelete && (
        <Modal className={"modal-wrapper max-w-[480px]"} isShow={isDeleteModalOpen}>
          <HeaderModal
            title={""}
            isShowHeader={true}
            className="modal-header pt-6 px-6"
          >
            <div className="flex items-center justify-between pb-7">
              <img
                className="h-10 w-10"
                src={allImgPaths.InfoIconBlue}
                alt="delete"
              />
              <img
                className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1 mb-4"
                id="modal-toggle"
                src={close}
                onClick={closeDeleteModal}
                alt="close icon"
              />
            </div>

            <h2 className="text-base text-[#0A090B] font-interSemiBold pb-1">
              Delete Team: {teamToDelete.name}
            </h2>
            <p className="text-sm text-[#475467] font-interRegular">
              Are you sure you want to delete this team? This action will permanently remove the team and all associated data including the Pinecone index. This cannot be undone.
            </p>
          </HeaderModal>

          <FooterModal
            title=""
            isShowFooter={true}
            className="modal-footer pt-5 px-6 pb-6"
          >
            <div className="flex items-center gap-x-3 justify-center">
              <button
                onClick={closeDeleteModal}
                type="button"
                className="text-[#4F4D55] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTeam}
                className={`!text-base text-white font-interSemiBold bg-red-600 hover:bg-red-700 rounded-lg px-3.5 py-2.5 flex-1 ${
                  isDeleting ? "button-disabled" : ""
                }`}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Team"}
              </button>
            </div>
          </FooterModal>
        </Modal>
      )}

      {/* Add Credit Modal */}
      {isModalOpen && selectedTeam && (
        <Modal className={"modal-wrapper max-w-[480px]"} isShow={isModalOpen}>
          <form onSubmit={handleAddCredit}>
            <HeaderModal
              title={""}
              isShowHeader={true}
              className="modal-header pt-6 px-6"
            >
              <div className="flex items-center justify-between pb-7">
                <img
                  className="h-10 w-10"
                  src={allImgPaths.InfoIconBlue}
                  alt="credit"
                />
                <img
                  className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1 mb-4"
                  id="modal-toggle"
                  src={close}
                  onClick={closeModal}
                  alt="close icon"
                />
              </div>

              <h2 className="text-base text-[#0A090B] font-interSemiBold pb-1">
                Add Credit to {selectedTeam.name}
              </h2>
              <p className="text-sm text-[#475467] font-interRegular">
                the credit will be added to the team balance and can be used.
              </p>
            </HeaderModal>

            <Body title="" isShowBody={true} className="modal-body px-6 pt-7">
              <div className="pb-5">
                {/* Credit Amount */}
                <div className="mb-6">
                  <label
                    htmlFor="creditAmount"
                    className="text-[#2D2B32] text-sm font-interMedium block pb-2"
                  >
                    Credit Amount
                  </label>
                  <InputTextField
                    className="placeholder:text-[#7F7D83] placeholder:text-[15px] text-base text-[#344054] w-full"
                    name={"creditAmount"}
                    type={"number"}
                    placeholder={"Enter amount"}
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                  />
                </div>

                {/* Message/Description */}
                <div>
                  <div className="flex justify-between pb-2">
                    <label
                      htmlFor="creditMessage"
                      className="text-[#2D2B32] text-sm font-interMedium block"
                    >
                      Description
                    </label>
                  </div>
                  <Textarea
                    name="creditMessage"
                    placeholder="Enter a description for this credit addition"
                    value={creditMessage}
                    onChange={(e) => setCreditMessage(e.target.value)}
                    id="creditMessage"
                  />
                </div>
              </div>
            </Body>

            <FooterModal
              title=""
              isShowFooter={true}
              className="modal-footer pt-5 px-6 pb-6"
            >
              <div className="flex items-center gap-x-3 justify-center">
                <button
                  onClick={closeModal}
                  type="button"
                  className="text-[#4F4D55] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
                >
                  Cancel
                </button>
                <button
                  className={`!text-base text-white font-interSemiBold bg-[#1E0AF5] hover:bg-[#113B98] rounded-lg px-3.5 py-2.5 flex-1 ${
                    !creditAmount || !creditMessage || isSubmitting
                      ? "button-disabled"
                      : ""
                  }`}
                  type="submit"
                  disabled={!creditAmount || !creditMessage || isSubmitting}
                >
                  {isSubmitting ? "Processing..." : "Add Credit"}
                </button>
              </div>
            </FooterModal>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default AddCreditToTeam;
